/** A3 coursework, private hand-in, materials and provider-record copy. */
export const a3Copy = {
  primaryPublishesResources: "The primary instructor publishes readings and the course guide. Your edits are kept here.",
  author: {
    addCriterion: "Add criterion",
    anySubmission: "Any submission",
    assessment: "Assessment",
    briefAndSettings: "Brief and settings",
    checksTheOriginalResultWithoutSendingTheWorkAgain: "Checks the original result without sending the work again.",
    compareDraft: "Compare draft",
    compareTheSavedDraftBeforeContinuing: "Compare the saved draft before continuing.",
    coursework: "Coursework",
    currentGradeItemDetailsUnavailable: "Current grade item (details unavailable)",
    currentSessionDetailsUnavailable: "Current session (details unavailable)",
    describeTheWorkLearnersWillDo: "Describe the work learners will do",
    discardDraft: "Discard draft",
    discardThisDraft: "Discard this draft",
    discarded: "Discarded",
    discardedDraftRetained: "Discarded draft retained",
    draft: "Draft",
    draftDiscardedItsWritingIsRetainedAndCanBeRestored: "Draft discarded. Its writing is retained. Restore it to continue editing privately.",
    draftKept: "Draft kept",
    draftRestoredPrivately: "Draft restored privately.",
    gradeItem: "Grade item",
    gradeItemsCouldNotBeRead: "Grade items could not be read.",
    gradingScale: "Grading scale",
    instructions: "Instructions",
    keepEditing: "Keep editing",
    keepMyWritingOverThisRevision: "Keep my writing over this revision",
    nextDrafts: "Next drafts",
    noDraftsOnThisPage: "No drafts on this page.",
    noFurtherInstructions: "No further instructions",
    none: "None",
    notConfirmed: "Not confirmed",
    notGraded: "Not graded",
    nothingToHandIn: "Nothing to hand in",
    onlyThePrimaryInstructorMayPublishCoursework: "Only the primary instructor may publish coursework.",
    openPublishedCoursework: "Open published coursework",
    points: "Points",
    previewLearnerBrief: "Preview learner brief",
    previousDrafts: "Previous drafts",
    privateDraft: "Private draft",
    privateUntilPublished: "Private until published",
    privateWritingIncludingRetainedDiscardedAndPublishedDrafts: "Private writing, including retained discarded and published drafts.",
    publishCoursework: "Publish coursework",
    published: "Published",
    publishedRevisionRetained: "Published revision retained",
    /** #1412: published terms nobody has corrected yet; an edit starts the private correction. */
    publishedTermsEditingStartsAPrivateCorrection: "These terms are published. Editing starts a private correction.",
    publishedThisSavedCourseworkRevisionToTheCourseSEnrolledLearners: "Published this saved coursework revision to the course's enrolled learners.",
    /** D20: work published on a draft course waits for the course's publication. */
    publishedThisSavedCourseworkRevisionUntilCoursePublication: "Published this saved coursework revision. Learners see it when the course is published.",
    learnersSeeItWhenTheCourseIsPublished: "Learners see it when the course is published.",
    quizLink: "Quiz link",
    readThisDraftBeforeContinuing: "Read this draft before continuing.",
    readingTeachingDrafts: "Reading teaching drafts",
    resolveTheCurrentDraftRequestBeforeContinuing: "Resolve the current draft request before continuing.",
    restoreDraft: "Restore draft",
    restoreThisDraftForPrivateEditing: "Restore this draft for private editing.",
    retainThisDraftSWritingAsDiscardedLearnersWillNotSeeIt: "Retain this draft's writing as discarded. Learners will not see it.",
    retained: "Retained",
    returnToThisDraftBeforePublishing: "Return to this draft before publishing.",
    returnToWriting: "Return to writing",
    rubric: "Rubric",
    saveAgain: "Save again",
    sayWhyThesePublishedTermsAreChanging: "Say why these published terms are changing.",
    session: "Session",
    sessionChoicesCouldNotBeRead: "Session choices could not be read.",
    settings: "Settings",
    state: "State",
    teachingDrafts: "Teaching drafts",
    thePrimaryInstructorPublishesThisCoursework: "The primary instructor publishes this coursework.",
    theRequestDidNotCommitYourWritingIsKeptReviewItThenSaveAgain: "The request did not commit. Your writing is kept. Review it, then save again.",
    thisBrowserCouldNotRetainYourNewestWritingKeepThisPageOpenUntilItIsSaved: "This browser could not retain your newest writing. Keep this page open until it is saved.",
    thisTermIsClosedTheDraftIsReadOnly: "This term is closed; the draft is read-only.",
    title: "Title",
    tryAgain: "Try again",
    untitledCoursework: "Untitled coursework",
    useSavedWriting: "Use saved writing",
    whatLearnersHandIn: "What learners hand in",
    whatThisChanges: "What this changes",
    wholeCourse: "Whole course",
    whyAreTheTermsChanging: "Why are the terms changing?",
    writePrivatelyAndPublishTheExactSavedCoursework: "Write privately and publish the exact saved coursework",
    writing: "Writing",
    yourTeachingDraftsCouldNotBeRead: "Your teaching drafts could not be read. ",
    yourWritingChangedOrItsSaveIsUnresolvedReviewTheCurrentDraftBeforePublishing: "Your writing changed or its save is unresolved. Review the current draft before publishing.",
    /** An unanswered request names its own act and checks it; it never resends (S1A-8). */
    uncertain: {
      save: { cap: "Save not confirmed", sentence: "Your writing is kept. Check whether it was saved.", check: "Check save" },
      publish: { cap: "Publication not confirmed", sentence: "Your writing is kept. Check whether this version was published.", check: "Check publication" },
      discard: { cap: "Discard not confirmed", sentence: "Your writing is kept. Check whether this draft was discarded.", check: "Check discard" },
      restore: { cap: "Restore not confirmed", sentence: "Your writing is kept. Check whether this draft was restored.", check: "Check restore" },
      rebase: { cap: "Rebase not confirmed", sentence: "Your writing is kept. Check whether this correction was rebased.", check: "Check rebase" },
    },
  },
  reference: "Material",
  handIns: "Hand-ins",
  handInCount: (submitted: number, enrolled: number) => `${submitted} of ${enrolled} handed in`,
  asOfRead: (time: string) => `As of ${time}`,
  criterionScore: (label: string, score: number | null | undefined, points: number) => score == null ? `${label}: Not recorded` : `${label} ${score} of ${points}`,
  handIn: "Hand in",
  readYourOwnAttemptsBeforeChoosingAHandIn: "Read your own attempts before choosing a hand-in.",
  yourAttemptsCouldNotBeRead: "Your attempts could not be read.",
  weCouldNotConfirmTheChangeCheckYourConnection: "We could not confirm the change. Check your connection and try again.",
  reviewAndHandIn: "Review and hand in",
  handInAttempt: (slot0: unknown) => "Hand in attempt " + String(slot0) + "",
  draftUnavailable: "Draft unavailable",
  theSavedWritingChangedCompareItBeforeReviewing: "The saved writing changed. Compare it before reviewing.",
  assignmentTermsUnavailable: "Assignment terms unavailable",
  handedInAttempt: (slot0: unknown) => "Handed in attempt " + String(slot0) + ".",
  yourInstructorCanNowReadThisAttempt: "Your instructor can now read this attempt.",
  handInNotConfirmed: "Hand-in not confirmed",
  yourWorkMayHaveArrivedCheckThisHandIn: "Your work may have arrived. Check this hand-in before doing anything else.",
  checkHandIn: "Check hand-in",
  thisReadsTheSameHandInItNeverSends: "This reads the same hand-in; it never sends another attempt.",
  notHandedIn: "Not handed in",
  noHandInWasRecordedYourWritingIsKept: "No hand-in was recorded. Your writing is kept; you can hand it in again.",
  thisWorkIsClosedToNewHandInsYour: "This work is closed to new hand-ins. Your writing is kept.",
  chooseWhichWritingToKeepBeforeHandingItIn: "Choose which writing to keep before handing it in.",
  chooseWhetherToUseYourRecoveredWritingFirst: "Choose whether to use your recovered writing first.",
  yourWritingHasNotBeenSavedResolveItsSave: "Your writing has not been saved. Resolve its save before handing it in.",
  writeYourResponseFirst: "Write your response first.",
  whenYouHandIn: "When you hand in",
  sendTheWritingShownHereToYourInstructorAs: "Send the writing shown here to your instructor as this attempt.",
  resolveTheWritingSBlockerBeforeHandingItIn: "Resolve the writing's blocker before handing it in.",
  yourSavedWritingIsNoLongerAvailable: "Your saved writing is no longer available.",
  theTermsChangedReadTheCurrentBriefBeforeHanding: "The terms changed. Read the current brief before handing in; your writing is kept.",
  theSavedWritingChangedCompareTheCopiesYourWriting: "The saved writing changed. Compare the copies; your writing is kept.",
  openingYourPrivateWriting: "Opening your private writing...",
  startWriting: "Start writing",
  startPrivateWriting: "Start a private draft. Your instructor sees it when you hand it in.",
  addPrivateReference: "Add your reference to save a private draft.",
  writingIsUnavailable: "Writing is unavailable.",
  retryWriting: "Retry writing",
  yourWritingIsNotSavedToIMAM: "Your writing is not saved to IMAM",
  saveCurrentWriting: "Save current writing",
  privateWriting: "Private writing",
  privateDraftErased: "Private draft erased",
  recoveredWritingIsReadyToCompare: "Recovered writing is ready to compare",
  savingToIMAMSavedInThisBrowser: "Saving to IMAM... Saved in this browser.",
  savingToIMAMSavingRecoveryInThisBrowser: "Saving to IMAM... Saving recovery in this browser...",
  notSavedToIMAM: (slot0: unknown, slot1: unknown) => "Not saved to IMAM" + String(slot0) + ". " + String(slot1) + "",
  savedInThisBrowserYourChangesRemainInThis: "Saved in this browser; your changes remain in this page.",
  aTemporaryBrowserCopyMayBeLostIfThe: "A temporary browser copy may be lost if the browser crashes.",
  yourChangesRemainInThisPage: "Your changes remain in this page.",
  savedInThisBrowserWaitingToSaveToIMAM: "Saved in this browser. Waiting to save to IMAM.",
  savingRecoveryInThisBrowser: "Saving recovery in this browser...",
  temporaryBrowserCopyCrashRecoveryIsUnavailable: "Temporary browser copy. Crash recovery is unavailable.",
  unsavedChangesInThisPage: "Unsaved changes in this page",
  savedToIMAM: "Saved to IMAM ",
  privateToYouUntilYouHandItIn: " · Private to you until you hand it in",
  privateToYouUntilYouHandItIn2: "Private to you until you hand it in · ",
  thisPrivateDraftWasErasedWhenItsCourseWas: "This private draft was erased when its course was archived. What you handed in stays in this box.",
  thisWorkNoLongerTakesHandInsDownloadYour: "This work no longer takes hand-ins. Download your writing to keep a copy.",
  downloadYourWriting: "Download your writing",
  recoveredWriting: "Recovered writing",
  writingRecoveredFromThisBrowser: "Writing recovered from this browser",
  thisWritingWasNotConfirmedSavedToIMAMRead: "This writing was not confirmed saved to IMAM. Read it before choosing. Use recovered writing to continue and save it to IMAM. If the saved revision changed, choose which copy to keep first.",
  thisTemporaryBrowserCopyIsAvailableNowButMay: "This temporary browser copy is available now, but may not survive a browser crash.",
  discardRecoveredWriting: "Discard recovered writing",
  theServerHasADifferentRevisionChooseWhichWriting: "The server has a different revision. Choose which writing to keep.",
  useRecoveredWriting: "Use recovered writing",
  yourUnsavedWritingIsKeptInThisBrowserFor: "Your unsaved writing is kept in this browser for your return. Signing out removes that copy.",
  keepThisPageOpenOrDownloadYourWritingThe: "Keep this page open or download your writing. The temporary browser copy may be lost in a crash.",
  keepThisPageOpenOrDownloadYourWritingBrowser: "Keep this page open or download your writing. Browser recovery is unavailable; unsaved changes are only available on this page.",
  resolveWritingConflict: "Resolve writing conflict",
  aNewerVersionWasSavedElsewhere: "A newer version was saved elsewhere",
  yourWritingIsStillInTheEditorCompareIt: "Your writing is still in the editor. Compare it with the version saved ",
  beforeChoosingWhichToKeep: " before choosing which to keep.",
  readSavedVersion: "Read saved version",
  useSavedVersion: "Use saved version",
  keepMyWritingAndSave: "Keep my writing and save",
  due: (slot0: unknown, slot1: unknown) => "" + String(slot0) + " · Due " + String(slot1) + "",
  notSet: "not set",
  yourWritingAsItGoes: "Your writing as it goes",
  yourWriting: "Your writing",
  itGoesToYourInstructorExactlyAsShownYou: "It goes to your instructor exactly as shown. You can hand in again only if it is returned for revision.",
  newHandInsAreUnavailableYourWritingRemainsIn: "New hand-ins are unavailable. Your writing remains in this page.",
  notConfirmedYourWorkMayOrMayNotHave: "Not confirmed. Your work may or may not have arrived.",
  keepThisReviewOpenAndCheckTheSameHand: "Keep this review open and check the same hand-in again. If it arrived, you get that receipt, never a second attempt.",
  notHandedInTheTermsChangedWhileYouWere: (slot0: unknown) => "Not handed in. The terms changed " + String(slot0) + " while you were reviewing.",
  notHandedIn2: (slot0: unknown) => "Not handed in. " + String(slot0) + "",
  nothingWasHandedInReturnToYourWritingThen: "Nothing was handed in. Return to your writing, then review the current terms again.",
  returnToWriting: "Return to writing",
  keepWriting: "Keep writing",
  handingIn: "Handing in...",
  checkAgain: "Check again",
  readingThisWork: "Reading this work.",
  tryAgain: "Try again",
  asOf: "As of ",
  refresh: "Refresh",
  readTheExactNativeWorkAndItsReceipt: "Read the exact native work and its receipt.",
  thisWorkOrItsExactReceiptCouldNotBe: "This work or its exact receipt could not be read.",
  theLatestCourseworkCouldNotBeReadALater: "The latest coursework could not be read. A later change is not shown.",
  courseworkCouldNotBeRead: "Coursework could not be read.",
  noCourseworkMatchesYourSearch: "No coursework matches your search.",
  noCourseworkHasBeenPublishedYet: "No coursework has been published yet.",
  noNativeCourseworkHasBeenPublished: "No native coursework has been published.",
  due2: (slot0: unknown) => "Due " + String(slot0) + "",
  noDueDate: "No due date",
  coursework: "Coursework",
  findNativeCourseworkAndDoItsWorkInThe: "Find native coursework and do its work in the row that names it.",
  thisTermIsClosedExistingWorkAndFeedbackRemain: "This term is closed. Existing work and feedback remain readable; new hand-ins are not available.",
  nativeCoursework: "Native coursework",
  yourCoursework: "Your coursework",
  findALearnerInThisWork: "Find a learner in this work",
  findCourseworkByTitleDueDateOrState: "Find coursework by title, due date or state",
  setNewCoursework: "Set new coursework",
  readingTheSelectedWork: "Reading the selected work",
  returnedForRevision: "Returned for revision",
  handedInAwaitingFeedback: "Handed in, awaiting feedback",
  handedInAgainAwaitingFeedback: "Handed in again, awaiting feedback",
  returned: "Returned",
  feedbackReleased: "Feedback released",
  closedToNewHandIns: "Closed to new hand-ins",
  toReview: (slot0: unknown) => "" + String(slot0) + " to review",
  review: (slot0: unknown) => "Review " + String(slot0) + "",
  setWork: "Set work",
  openWork: "Open work",
  noFurtherInstructions: "No further instructions.",
  selectedWork: "Selected work",
  thisCourseworkAddressIsUnavailable: "This coursework address is unavailable.",
  backToCoursework: "Back to coursework",
  compareTheNewerWorkWithYourDraftAndChoose: "Compare the newer work with your draft and choose which to keep.",
  sayWhyTheTermsChanged: "Say why the terms changed.",
  chooseACurrentSessionPlacementBeforeRecording: "Choose a current session placement before recording.",
  chooseACurrentGradeItemBeforeRecording: "Choose a current grade item before recording.",
  recordOrDiscardYourChangedTermsBeforeChangingThis: "Record or discard your changed terms before changing this work's status.",
  publishToThisCourseSEnrolledLearnersTheyCan: (slot0: unknown) => "Publish " + String(slot0) + " to this course's enrolled learners. They can begin their own attempts.",
  publishUntilCoursePublication: (slot0: unknown) => "Publish " + String(slot0) + ". Enrolled learners see it and can begin their own attempts when the course is published.",
  closeToNewHandInsExistingAttemptsAndFeedback: (slot0: unknown) => "Close " + String(slot0) + " to new hand-ins. Existing attempts and feedback stay readable, and the teaching team can still grade them.",
  removeTheUnpublishedDraftNoLearnerHasBeenGiven: (slot0: unknown) => "Remove the unpublished draft " + String(slot0) + ". No learner has been given this work.",
  recordCorrectedTermsForEarlierTermsAndTheAttempts: (slot0: unknown) => "Record corrected terms for " + String(slot0) + ". Earlier terms and the attempts that answered them stay in history.",
  keepAsATeachingDraftLearnersDoNotSee: (slot0: unknown) => "Keep " + String(slot0) + " as a teaching draft. Learners do not see it.",
  createAsAnUnpublishedTeachingDraft: (slot0: unknown) => "Create " + String(slot0) + " as an unpublished teaching draft.",
  courseworkChangeNotConfirmed: "Coursework change not confirmed",
  theChangeMayHaveBeenRecordedCheckThisRequest: "The change may have been recorded. Check this request before changing your draft.",
  checkCourseworkChange: "Check coursework change",
  checkReadsTheOriginalReceiptWithoutSendingAnotherChange: "Check reads the original receipt without sending another change.",
  notRecorded: "Not recorded",
  noChangeWasRecordedYourDraftIsKept: "No change was recorded. Your draft is kept.",
  changeCoursework: "Change coursework",
  setCoursework: "Set coursework",
  reviewAndExplicitlyRecordNativeCourseworkTermsOrStatus: "Review and explicitly record native coursework terms or status.",
  changedNotRecorded: "Changed, not recorded",
  teachingDraft: "Teaching draft",
  publishedTerms: "Published terms",
  brief: "Brief",
  yourTerms: "Your terms",
  newerWorkIsOnRecordYourDraftIsKept: "Newer work is on record. Your draft is kept.",
  titleOnRecord: "Title on record",
  instructionsOnRecord: "Instructions on record",
  none: "None",
  statusOnRecord: "Status on record",
  keepMine: "Keep mine",
  useTheOneOnRecord: "Use the one on record",
  change: "Change",
  correctTheTerms: "Correct the terms",
  saveDraftTerms: "Save draft terms",
  publishToLearners: "Publish to learners",
  removeDraft: "Remove draft",
  closeToNewHandIns: "Close to new hand-ins",
  title: "Title",
  instructions: "Instructions",
  assessment: "Assessment",
  quizLink: "Quiz link",
  whatLearnersHandIn: "What learners hand in",
  writing: "Writing",
  aLink: "A link",
  aPrivateFile: "A private file",
  nothingToHandIn: "Nothing to hand in",
  points: "Points",
  due3: "Due (",
  rubricOptional: "Rubric (optional)",
  criterion: (slot0: unknown) => "Criterion " + String(slot0) + "",
  criterionPoints: (slot0: unknown) => "Criterion " + String(slot0) + " points",
  removeCriterion: "Remove criterion",
  addCriterion: "Add criterion",
  whyDidTheTermsChange: "Why did the terms change?",
  discardChanges: "Discard changes",
  placement: "Where it appears",
  courseScope: "Course scope",
  session: "Session",
  wholeCourse: "Whole course",
  gradeItem: "Grade item",
  notGraded: "Not graded",
  sessionChoicesCouldNotBeRead: "Session choices could not be read.",
  gradeItemsCouldNotBeRead: "Grade items could not be read.",
  publishCoursework: "Publish coursework",
  closeCoursework: "Close coursework",
  recordCorrection: "Record correction",
  createDraftCoursework: "Create draft coursework",
  whenYouRecordThisCoursework: "When you record this coursework",
  lastSuccessfulRead: "Last successful read",
  lastRead: "Last read",
  lastReadFailed: (slot0: unknown) => "Last read failed " + String(slot0) + "",
  submissionHistory: "Submission history",
  noSubmissionHistoryWasRecorded: "No submission history was recorded.",
  due4: "Due ",
  points2: " points",
  publicationObserved: "Publication observed ",
  assignmentMaterials: "Assignment materials",
  readingsAndMaterials: "Readings and materials",
  assignmentMaterial: "Assignment material",
  openMaterial: "Open material",
  notReadFromClassroomYet: "Not read from Classroom yet.",
  theLatestReadFailedALaterChangeIsNot: "The latest read failed. A later change is not shown.",
  personalDocument: "Personal document",
  classroomGrade: "Classroom grade: ",
  classroomDraftGrade: "Classroom draft grade: ",
  thatStoredRecordIsUnavailable: "That stored record is unavailable.",
  readTheExactStoredClassroomInstructionsAndAttachments: "Read the exact stored Classroom instructions and attachments.",
  assignmentBrief: "Assignment brief",
  noAdditionalInstructions: "No additional instructions.",
  readEachAuthorizedLearnerSStoredClassroomWorkInside: "Read each authorized learner's stored Classroom work inside that learner's row.",
  yourHistoricalClassroomWork: "Your historical Classroom work",
  learners: "Learners",
  noSubmissionRecorded: "No submission recorded.",
  noLearnerSubmissionsWereRecorded: "No learner submissions were recorded.",
  storedClassroomRecords: "Stored Classroom records",
  yourWork: "Your work",
  learnerSubmission: "Learner submission",
  readStoredWork: "Read stored work",
  institutionalResultsAreApprovedSeparately: "Institutional results are approved separately.",
  readTheExactStoredClassroomRecord: "Read the exact stored Classroom record.",
  storedRecord: "Stored record",
  theStoredRecordCouldNotBeRead: "The stored record could not be read.",
  topic: "Topic",
  material: "Material",
  announcement: "Announcement",
  classroomHasNoTopicsMaterialsOrAnnouncementsForThis: "Classroom has no topics, materials or announcements for this course.",
  classroomReferencesCouldNotBeLoaded: "Classroom references could not be loaded.",
  refreshDidNotComplete: "Refresh did not complete.",
  classroomRefusedAccessToThisCourseSStream: "Classroom refused access to this course's stream.",
  classroomCouldNotBeRead: "Classroom could not be read.",
  notReadYet: "Not read yet.",
  fromClassroom: "From Classroom",
  readSavedClassroomTopicsMaterialsAndAnnouncements: "Read saved Classroom topics, materials and announcements.",
  savedReferences: "Saved references",
  refreshing: "Refreshing",
  someLearners: "Some learners",
  audienceNotConfirmed: "Audience not confirmed",
  show: "Show",
  noMaterialsInThisTopic: "No materials in this topic.",
  driveFilesFor: (slot0: unknown) => "Drive files for " + String(slot0) + "",
  driveFile: "Drive file",
  reissuedInIMAM: "Reissued in IMAM",
  closedHere: "Closed here",
  recordedInIMAM: "Recorded in IMAM",
  thisItem: "This item",
  classroomIsUnchangedItsStoredRecordStaysReadableHere: "Classroom is unchanged. Its stored record stays readable here.",
  retryFailed: (slot0: unknown) => "Retry " + String(slot0) + " failed",
  onlyTheFailedItemsAreSentWithTheSame: "Only the failed items are sent with the same request key.",
  decisionNotConfirmed: "Decision not confirmed",
  theDecisionMayHaveBeenRecordedCheckItsReceipt: "The decision may have been recorded. Check its receipt before changing it.",
  checkDecision: "Check decision",
  thisReadsTheOriginalReceiptWithoutSendingTheDecision: "This reads the original receipt without sending the decision again.",
  noDecisionWasRecordedYourEntriesAreKept: "No decision was recorded. Your entries are kept.",
  theStoredClassroomRecordsCouldNotBeRefreshedA: "The stored Classroom records could not be refreshed. A later change is not shown.",
  postedInClassroomCouldNotBeRead: "Posted in Classroom could not be read.",
  postedCoursework: "Posted coursework",
  noPostedCourseworkStored: "No posted coursework is stored.",
  recordingChange: "Recording",
  notConfirmed: "Not confirmed",
  partlyDone: "Partly done",
  storedDecisionFactsChanged: "The stored facts changed. Compare them with your decision before recording.",
  readClassroomSStoredCourseworkAndDecideWhatIMAM: "Read Classroom's stored coursework and decide what IMAM will do with it.",
  toSettleInIMAM: (slot0: unknown) => " · " + String(slot0) + " to settle in IMAM",
  notRead: (slot0: unknown) => " · " + String(slot0) + " not read",
  readOnlyHistoryTheTermIsClosed: "Read-only history. The term is closed.",
  toDecide: (slot0: unknown) => "" + String(slot0) + " to decide",
  storedRecords: "Stored records",
  readStoredCoursework: "Read stored coursework",
  couldnTRead: "Couldn't read",
  notRead2: "Not read",
  toDecide2: "To decide",
  read: (slot0: unknown) => "Read " + String(slot0) + "",
  decide: "Decide",
  select: (slot0: unknown) => "Select " + String(slot0) + "",
  select2: "Select",
  read2: "Read ",
  inThisCourse: " in this course",
  thisStoredItemNoLongerPermitsThatDecisionRead: "This stored item no longer permits that decision. Read its current record.",
  readTheCurrentStoredRecordsBeforeDeciding: "Read the current stored records before deciding.",
  reissueInIMAM: "Reissue in IMAM",
  closeHere: "Close here",
  whenYouRecordThisIMAMDecision: "When you record this IMAM decision",
  createAsAnUnpublishedIMAMDraftAndSettleThis: (slot0: unknown) => "Create " + String(slot0) + " as an unpublished IMAM draft and settle this stored item. Classroom is unchanged.",
  thisWork: "this work",
  closeHereItsClassroomRecordStaysReadableAndClassroom: (slot0: unknown) => "Close " + String(slot0) + " here. Its Classroom record stays readable and Classroom is unchanged.",
  closeSelectedHere: "Close selected here",
  closeOnlyTheStoredClassroomItemsExplicitlySelectedIn: "Close only the stored Classroom items explicitly selected in the List.",
  selectedNotClosed: "Selected, not closed",
  selectedItems: "Selected items",
  closeHere2: (slot0: unknown) => "Close " + String(slot0) + " here",
  whenYouCloseTheseItemsHere: "When you close these items here",
  closeInIMAMClassroomAndItsStoredRecordAre: (slot0: unknown) => "Close " + String(slot0) + " in IMAM. Classroom and its stored record are unchanged.",
  needsACurrentReadWithNoTurnInsBefore: (slot0: unknown) => "" + String(slot0) + " needs a current read with no turn-ins before it can close in this selection.",
  iMAMDecision: "IMAM decision",
  due5: "Due",
  readingSessionChoices: "Reading session choices.",
  sessionChoicesCouldNotBeReadKeepTheCurrent: "Session choices could not be read. Keep the current placement or try again before changing it.",
  materialChangeNotConfirmed: "Material change not confirmed",
  theChangeMayHaveBeenRecordedCheckThisRequest2: "The change may have been recorded. Check this request before changing the draft.",
  checkMaterialChange: "Check material change",
  checkReadsTheOriginalReceiptAndNeverSendsThe: "Check reads the original receipt and never sends the change again.",
  noMaterialChangeWasRecordedYourDraftIsKept: "No material change was recorded. Your draft is kept.",
  thisCourseSMaterialsAreUnavailable: "This course's materials are unavailable.",
  backToCourse: "Back to course",
  thisMaterialIsUnavailable: "This material is unavailable.",
  backToMaterials: "Back to materials",
  theLatestMaterialsCouldNotBeReadALater: "The latest materials could not be read. A later change is not shown.",
  noSyllabusHasBeenPublished: "No syllabus has been published.",
  noCourseMaterialsMatchYourSearch: "No course materials match your search.",
  noCourseMaterialsHaveBeenPublished: "No course materials have been published.",
  theCourseSMaterialsCouldNotBeRead: "The course's materials could not be read.",
  readOrPublishTheCourseSOneSyllabusReference: "Read or publish the course's syllabus.",
  readAndMaintainTheCourseSMaterialsInTheir: "Read and maintain the course's materials in their teaching sequence.",
  syllabus: "Syllabus",
  courseMaterials: "Course materials",
  noSyllabusYet: "No syllabus yet",
  noCourseMaterialsYet: "No course materials yet",
  findCourseMaterials: "Find course materials",
  addSyllabus: "Add syllabus",
  addMaterial: "Add material",
  titleNeeded: "Title needed",
  published: "Published",
  draft: "Draft",
  sessionPlacement: "Session",
  googleDrive: "Google Drive",
  link: "Link",
  editMaterial: "Edit material",
  addReference: "Add material",
  readReference: "Read material",
  readTheCurrentMaterialsBeforeRecordingAChange: "Read the current materials before recording a change.",
  compareTheNewerMaterialWithYourDraftAndChoose: "Compare the newer material with your draft and choose which to keep.",
  theSelectedSessionPlacementIsUnavailableChooseACurrent: "The selected session is unavailable. Choose a current session before recording.",
  recordTheReferenceCorrectionBeforeChangingItsSessionPlacement: "Record the material's correction before moving it to another session.",
  publishedMaterialsStayInHistoryCorrectThisReferenceInstead: "Published materials stay in history. Correct this material instead of removing it.",
  moveReference: "Move material",
  removeMaterial: "Remove material",
  publishReference: "Publish material",
  saveChanges: "Save changes",
  addDraftReference: "Add draft material",
  whenYouRecordThisMaterial: "When you record this material",
  moveToItsPublishedContentStaysTheSame: (slot0: unknown, slot1: unknown) => "Move " + String(slot0) + " to " + String(slot1) + ". Its published content stays the same.",
  removeTheDraftReferenceTheFileItselfAndIts: (slot0: unknown) => "Remove the draft material " + String(slot0) + ". The file itself and its host's sharing are unchanged.",
  correctForEnrolledLearnersKeepTheEarlierReferenceIn: (slot0: unknown) => "Correct " + String(slot0) + " for enrolled learners. Keep the earlier material in the course's history.",
  publishToEnrolledLearnersTheFileSHostStill: (slot0: unknown, slot1: unknown) => "Publish " + String(slot0) + " to enrolled learners" + String(slot1) + ". The file's host still decides access.",
  aNewerMaterialIsOnRecordYourDraftIs: "A newer material is on record. Your draft is kept.",
  referenceOnRecord: "Material on record",
  noReference: "No link",
  changeThisMaterial: "Change this material",
  publishNowOrKeepAsADraft: "Publish now or keep as a draft",
  correctPublishedReference: "Correct published material",
  keepATeachingDraft: "Keep a teaching draft",
  changeSessionPlacement: "Move to another session",
  removeDraftReference: "Remove draft material",
  typeOptional: "Type (optional)",
  referenceURL: "Link",
  googleDriveOpensInYourOwnDriveThisCourse: "Google Drive opens in your own Drive. This course's folder, including its session folders, is a second tab.",
  sessionChoicesCouldNotBeRead2: "Session choices could not be read. ",
  readSessionChoicesAgain: "Read session choices again",
  courseReference: "Course material",
  open: "Open ",
  withItsHost: " with its host",
  opensInANewTab: " (opens in a new tab)",
  inCoursework: " in coursework",
  thisReferenceIsUnavailable: "This material is unavailable.",
  session2: (slot0: unknown) => "Session " + String(slot0) + "",
  workForThisSession: "Work for this session",
  readTheSessionSNativeWork: "Read the session's native work.",
  yourWorkForThisSessionCouldNotBeRead: "Your work for this session could not be read.",
  noWorkIsSetForThisSession: "No work is set for this session.",
  noHandInWasRecordedForThisRequestYour: "No hand-in was recorded for this request. Your entry is kept.",
  thatUploadAlreadyBelongsToAHandInRead: "That upload already belongs to a hand-in. Read your attempts before choosing another file.",
  thePrivateUploadCouldNotBeReservedYourFile: "The private upload could not be reserved. Your file is kept here.",
  theFileUploadWasNotConfirmedYourSelectedFile: "The file upload was not confirmed. Your selected file is kept. Retry its upload before handing in.",
  theFileCouldNotBePrepared: "The file could not be prepared.",
  thisWorkIsClosedToNewHandInsYour2: "This work is closed to new hand-ins. Your entry is kept.",
  readYourPrivateDraftBeforeHandingIn: "Read your private draft before handing in.",
  readYourCurrentPrivateDraftBeforeHandingInYour: "Read your current private draft before handing in. Your entry is kept.",
  compareYourEntryWithTheSavedReferenceFirst: "Compare your entry with the saved reference first.",
  checkYourPrivateSaveBeforeHandingIn: "Check your private save before handing in.",
  waitForYourFileToBePrepared: "Wait for your file to be prepared.",
  retryTheSelectedFileSUploadBeforeHandingIn: "Retry the selected file's upload before handing in.",
  thisPrivateUploadExpiredChooseYourFileAgain: "This private upload expired. Choose your file again.",
  readTheCurrentWorkSTermsFirst: "Read the current work's terms first.",
  enterYourLinkFirst: "Enter your link first.",
  yourLinkMustBeACompleteHttpOrHttps: "Your link must be a complete http or https address.",
  chooseYourFileFirst: "Choose your file first.",
  yourLink: "Your link",
  yourFile: "Your file",
  preparingYourPrivateFile: "Preparing your private file...",
  privateFileReferenceSavedHandInVerifiesTheFile: "Private file reference saved. Hand in verifies the file before accepting this attempt.",
  chooseAFileToPrepareItPrivatelyBeforeHanding: "Choose a file to prepare it privately before handing in.",
  readingYourPrivateDraft: "Reading your private draft...",
  savingYourPrivateReference: "Saving your private reference...",
  privateSaveNotConfirmedYourEntryStaysHere: "Private save not confirmed. Your entry stays here.",
  savedToIMAMPrivateToYouUntilYouHand: "Saved to IMAM. Private to you until you hand in.",
  yourPrivateDraftIsUnavailable: "Your private draft is unavailable.",
  readPrivateDraftAgain: "Read private draft again",
  checkPrivateSave: "Check private save",
  retryPrivateSave: "Retry private save",
  retryFileUpload: "Retry file upload",
  thisFileIsLargerThanChooseASmaller: (slot0: unknown) => "This file is larger than " + String(slot0) + " MB. Choose a smaller file.",
  imamAcceptsChooseAnotherFile: (slot0: unknown) => "IMAM accepts " + String(slot0) + ". Choose another file.",
  imamAcceptsUpTo: (slot0: unknown, slot1: unknown) => "IMAM accepts " + String(slot0) + " up to " + String(slot1) + " MB.",
  checkingThatYourFileArrived: "Checking that your file arrived...",
  waitWhileYourFileIsChecked: "Wait while your file is checked.",
  yourFileArrivedHandInVerifiesIt: "Your file arrived. Hand in verifies it before accepting this attempt.",
  uploadNotConfirmedChooseYourFileAgain: "Upload not confirmed. This file did not finish uploading. Choose it again.",
  chooseYourFileAgainBeforeHandingIn: "Choose your file again before handing in.",
  yourFileCouldNotBeCheckedYourReferenceIsKept: "Your file could not be checked. Your saved reference is kept.",
  checkThatYourFileArrivedBeforeHandingIn: "Check that your file arrived before handing in.",
  checkFileAgain: "Check file again",
  removeFile: "Remove file",
  chooseFromGoogleDrive: "Choose from Google Drive",
  openingGoogleDrive: "Opening Google Drive...",
  cancelOpeningGoogleDrive: "Cancel opening Google Drive",
  googleDriveIsUnavailablePasteYourLinkInstead: "Google Drive is unavailable right now. Paste your link instead.",
  yourTeachingTeamMustBeAbleToOpenThisLink: "Your teaching team must be able to open this link. Its host decides who can.",
  googleDecidesWhoCanOpenThisFileYourTeaching: "Google decides who can open this file. Your teaching team must be able to open it.",
  savedReferenceChanged: "Saved reference changed",
  theSavedReferenceChangedYourEntryIsKeptCompare: "The saved reference changed. Your entry is kept. Compare it before choosing.",
  anotherPrivateFileReference: "Another private file reference",
  keepMyEntry: "Keep my entry",
  useSavedReference: "Use saved reference",
  yourPrivateDraftHasNotBeenRead: "Your private draft has not been read.",
  handInAttemptWithTheSameLinkYourTeacher: (slot0: unknown) => "Hand in attempt " + String(slot0) + " with the same link. Your teacher reads what it shows now.",
  sendTheSavedLinkShownHereToYourInstructor: "Send the saved link shown here to your instructor for review.",
  sendToYourInstructorForReview: (slot0: unknown) => "Send " + String(slot0) + " to your instructor for review.",
  thePrivateFile: "the private file",
  resolveTheHandInSBlockerFirst: "Resolve the hand-in's blocker first.",
  yourPrivateSaveMustBeConfirmedFirstYourEntry: "Your private save must be confirmed first. Your entry is kept.",
  missing: "Missing",
  assessedInClass: "Assessed in class",
  yourScore: "Your score",
  feedbackOnAttempt: (slot0: unknown) => "Feedback on attempt " + String(slot0) + "",
  nothingWasHandedInByTheDueTime: "Nothing was handed in by the due time.",
  itCounts0: " It counts 0",
  on: " on ",
  itsLine: "its line",
  theWorkIsStillOpenYouCanHandIt: " The work is still open: you can hand it in, and your instructor reviews it then.",
  earlierWorkAndFeedback: "Earlier work and feedback",
  earlierAttempts: "Earlier attempts",
  markedMissingByYourInstructor: "Marked Missing by your instructor",
  attempt: "Attempt ",
  recordedByYourInstructor: "Recorded by your instructor",
  handedIn: (slot0: unknown) => "Handed in " + String(slot0) + "",
  showAttempt: "Show attempt ",
  theTermsChanged: "The terms changed ",
  reviewTheCurrentBriefBeforeHandingIn: ". Review the current brief before handing in.",
  showBrief: "Show brief",
  hideBrief: "Hide brief",
  theQuiz: "The quiz",
  openTheQuiz: "Open the quiz ↗",
  onItsHost: " on its host",
  itOpensOn: "It opens on ",
  itsHostGovernsAccessWhenYouHaveFinishedMark: "Its host governs access. When you have finished, Mark as taken tells your instructor, who enters your score here.",
  yourInstructorAssessesThisInClassAndRecordsYour: "Your instructor assesses this in class and records your attempt here, with your score.",
  thereIsNoFileOrWritingToHandIn: "There is no file or writing to hand in. Mark done tells your instructor you completed the work; your instructor records the result.",
  isClosedSoWorkCannotBeHandedInTo: (slot0: unknown) => "" + String(slot0) + " is closed, so work cannot be handed in to its assignments. You can still read your work and feedback.",
  thisTerm: "This term",
  thisWorkIsClosedToNewAttemptsYourInstructor: "This work is closed to new attempts. Your instructor can still grade it.",
  thisWorkIsNotOpenYet: "This work is not open yet.",
  recordedByYourInstructorNoHandIn: "Recorded by your instructor, no hand-in",
  youDidNotMarkThisQuizAsTakenYour: "You did not mark this quiz as taken. Your instructor recorded this attempt and its score from the quiz's own results.",
  nothingWasHandedInYourInstructorRecordedThisAttempt: "Nothing was handed in. Your instructor recorded this attempt.",
  quizCompletedElsewhere: "Quiz, completed elsewhere",
  markedDone: "Marked done",
  youMarkedItAs: "You marked it as ",
  at: " at ",
  yourInstructorEntersYourScoreFromTheQuizS: " Your instructor enters your score from the quiz's own results.",
  yourInstructorRecordsTheResult: " Your instructor records the result.",
  withYourInstructorYouCanHandInAgainOnly: "With your instructor. You can hand in again only if it is returned for revision.",
  whatYouHandedInAttempt: "What you handed in · attempt ",
  thisWorkIsUnavailable: "This work is unavailable.",
  readYourExactWorkAndReleasedFeedback: "Read your exact work and released feedback.",
  couldNotBeRead: (slot0: unknown) => "" + String(slot0) + " could not be read.",
  readBeforeHandingIn: "Read before handing in",
  feedback: "Feedback",
  released: "Released",
  readTheBriefRevisePrivateWritingAndHandIn: "Read the brief, revise private writing and hand in the exact saved revision.",
  theLatestBriefCouldNotBeReadYourWriting: "The latest brief could not be read. Your writing stays here.",
  handedIn2: "Handed in",
  reviseAttempt: (slot0: unknown) => "Revise attempt " + String(slot0) + "",
  addYourLink: "Add your link",
  chooseYourFile: "Choose your file",
  writeYourAnswer: "Write your answer",
  privateToYou: "Private to you",
  readTheCurrentBriefBeforeHandingIn: "Read the current brief before handing in.",
  youMarkedThisQuizAsTaken: "You marked this quiz as taken.",
  youMarkedThisWorkDone: "You marked this work done.",
  yourInstructorRecordsTheAcademicResult: "Your instructor records the academic result.",
  markNotConfirmed: "Mark not confirmed",
  yourMarkMayHaveArrivedCheckThisRequestBefore: "Your mark may have arrived. Check this request before doing anything else.",
  checkYourMark: "Check your mark",
  checkReadsTheReceiptWithoutSendingAnotherAttempt: "Check reads the receipt without sending another attempt.",
  noMarkWasRecordedYouCanReviewThisWork: "No mark was recorded. You can review this work and mark it again.",
  markYourOwnCompletionWithoutClaimingAnAcademicResult: "Mark your own completion without claiming an academic result.",
  theLatestWorkCouldNotBeReadReadIt: "The latest work could not be read. Read it before marking completion.",
  readTheBrief: "Read the brief",
  yourCompletion: "Your completion",
  yourOwnMark: "Your own mark",
  markAsTaken: "Mark as taken",
  markDone: "Mark done",
  whenYouRecordYourMark: "When you record your mark",
  tellYourInstructorThatYouTookTheQuizIts: "Tell your instructor that you took the quiz. Its host owns the answers, and your instructor records the score here.",
  tellYourInstructorThatYouCompletedThisWorkYour: "Tell your instructor that you completed this work. Your instructor records its result.",
  readTheCurrentBriefBeforeMarkingThisWork: "Read the current brief before marking this work.",
  readYourOwnWorkAndReleasedFeedback: "Read your own work and released feedback.",
  theLatestWorkCouldNotBeReadALater: "The latest work could not be read. A later change is not shown.",
  show2: (slot0: unknown) => "Show " + String(slot0) + "",
  yourPrivateReferenceIsUnavailableForThisAttemptYour: "Your private reference is unavailable for this attempt. Your entry stays here.",
  thisPrivateReferenceWasErasedYourEntryStaysHere: "This private reference was erased. Your entry stays here; this work cannot be handed in from that erased draft.",
  yourPrivateReferenceCouldNotBeRead: "Your private reference could not be read.",
  thatSaveWasNotRecordedYourEntryIsKept: "That save was not recorded. Your entry is kept; retry this same save before handing in.",
  theSaveAcknowledgmentIsIncompleteKeepYourEntryAnd: "The save acknowledgment is incomplete. Keep your entry and check again.",
  compareTheSavedReferenceWithYourEntryBeforeSaving: "Compare the saved reference with your entry before saving.",
  readYourPrivateDraftAgainBeforeSavingYourEntry: "Read your private draft again before saving. Your entry is kept.",
  readYourCurrentPrivateDraftBeforeHandingIn: "Read your current private draft before handing in.",
  saveNotConfirmedYourEntryStaysHereCheckThis: "Save not confirmed. Your entry stays here. Check this save before handing in.",
  yourReferenceWasNotSaved: "Your reference was not saved.",
  theSaveCouldNotBeChecked: "The save could not be checked.",
  theTermIsClosedSoAReturnIsNot: "The term is closed, so a return is not available. A grade can still be recorded.",
  thisWorkIsClosedToNewAttemptsSoA: "This work is closed to new attempts, so a return is not available. A grade can still be recorded.",
  readTheLatestReviewBeforeRecordingThisJudgement: "Read the latest review before recording this judgement.",
  theWorkSCurrentVersionCouldNotBeRead: "The work's current version could not be read. Refresh the review first.",
  compareYourJudgementWithTheOneOnRecordFirst: "Compare your judgement with the one on record first.",
  thisWorkTakesAScoreSoChooseAGrade: "This work takes a score, so choose a grade for it.",
  somethingWasHandedInOrAssessedInClassJudge: "Something was handed in or assessed in class. Judge that work instead of marking it missing.",
  thisLearner: "This learner",
  eachLearnerCanReadTheirOwnJudgement: "Each learner can read their own judgement.",
  theseJudgementsAreNotReleasedToLearners: "These judgements are not released to learners.",
  onlyTheFailedRecordsAreSentAgainWithThe: "Only the failed records are sent again, with the same request key.",
  judgementNotConfirmed: "Judgement not confirmed",
  theJudgementMayHaveBeenRecordedCheckThisSame: "The judgement may have been recorded. Check this same request before doing anything else.",
  checkJudgement: "Check judgement",
  checkReadsTheReceiptItNeverSendsTheJudgement: "Check reads the receipt; it never sends the judgement again.",
  noJudgementWasRecordedForThisRequestYourEntries: "No judgement was recorded for this request. Your entries are kept.",
  theLatestHandInsCouldNotBeReadA: "The latest hand-ins could not be read. A later change is not shown.",
  noLearnersAreOnThisReview: "No learners are on this review.",
  thisWorkSReviewCouldNotBeRead: "This work's review could not be read.",
  score: "Score",
  quickScoreFor: (slot0: unknown) => "Quick score for " + String(slot0) + "",
  of: "of ",
  readAndJudgeEachNativeAttemptInItsLearner: "Read and judge each native attempt in its learner's row.",
  review2: "Review",
  nothingWaitingForReview: "Nothing waiting for review",
  findALearner: "Find a learner",
  toReview2: "To review",
  roll: (slot0: unknown) => "Roll: " + String(slot0) + "",
  notMarked: "Not marked",
  whenYouRecordThisJudgement: "When you record this judgement",
  recordOnlyTheAttemptsSelectedInReview: "Record only the attempts selected in Review.",
  recordSelectedJudgements: "Record selected judgements",
  selectedNotRecorded: "Selected, not recorded",
  selectedAttempts: "Selected attempts",
  onlyTheseLearners: "Only these learners",
  judgement: "Judgement",
  selectedJudgement: "Selected judgement",
  recordEachRowSEntry: "Record each row's entry",
  markComplete: "Mark complete",
  markMissing: "Mark missing",
  releaseHeldJudgements: "Release held judgements",
  revisionOfAttemptTheReturnSaid: (n: number | null) => (n == null ? "A revision. " : "Revision of attempt " + String(n) + ". ") + "The return said:",
  heldMarkLeavesThroughRelease: "This attempt has a held mark, so your change is recorded held too. Release it with Release held judgements.",
  recordedHeldReleaserReleases: (who: string) => "You record it held; " + who + " releases it.",
  thePrimaryInstructor: "the primary instructor",
  aGrantedMarker: "a granted marker",
  or: " or ",
  levelsAreTwoToTen: "A level rubric has 2 to 10 levels.",
  nameEveryLevel: "Name every level.",
  eachLevelNeedsItsOwnName: "Each level needs its own name.",
  levelSharesAreDistinct: "Each level earns a different share from 0 to 100, and the top level earns 100.",
  sayWhatToLookFor: (label: string) => `Say what to look for in ${label}.`,
  levelPointsToTheHundredth: (label: string) => `Give ${label} points to the hundredth, up to 1000.`,
  markByLevels: "Mark by levels",
  markByLevelsHint: "Each criterion uses the same levels; a level earns its share of the criterion's points.",
  levels: "Levels",
  levelName: (n: number) => `Level ${n} name`,
  levelShare: (n: number) => `Level ${n} share (%)`,
  removeLevel: (n: number) => `Remove level ${n}`,
  addLevel: "Add level",
  whatToLookFor: (label: string) => `What to look for: ${label}`,
  whatEarnsLevel: (level: string, label: string, points: string) => `What earns ${level} in ${label} (${points}), optional`,
  nameThisLevel: "Name this level.",
  anotherLevelHasThisName: "Another level has this name.",
  giveAShareFrom0To100: "Give a share from 0 to 100.",
  anotherLevelEarnsThisShare: "Another level earns this share.",
  theTopLevelEarns100: "The top level earns 100.",
  levelsKeptAside: (names: string) => `${names} wait aside with what earns each, until you mark by levels again.`,
  criteriaTotal: (total: string | null) => total == null ? "The criteria's total, once each has points" : `${total}, the criteria's total`,
  levelsOfACriterion: (levels: string) => `Levels: ${levels} of a criterion's points, to the hundredth of a point.`,
  totalPoints: (total: string) => `Total ${total} ${total === "1" ? "point" : "points"}.`,
  recordMarkHeld: "Record mark, held",
  markedByLevel: "Mark by level",
  awardOtherPoints: "Award other points",
  whyThisAward: "Why this award",
  useTheLevelSPoints: (name: string, points: string) => `Use ${name}'s ${points}`,
  levelMarkTotal: (total: string | null, points: number | null) => total == null ? "Score: waits for every criterion" : `Score: ${total}${points != null ? ` of ${points}` : ""}`,
  markDraftSaved: (revision: number) => `Saved privately, revision ${revision}`,
  markDraftSaving: "Saving privately",
  markDraftNotSaved: (reason: string) => `Not saved privately: ${reason}`,
  markRecordedHeld: (name: string) => `${name}: mark recorded, held`,
  heldUntilReleased: "Held until it is released; only the teaching team reads it.",
  theMarkMayHaveBeenRecordedCheckIt: "The mark may have been recorded. Check it before recording again.",
  checkMark: "Check mark",
  noMarkWasRecordedForThisRequest: "No mark was recorded for this request; your entries are kept here.",
  levelMarksRecordOneByOne: "Record each level mark from its learner's row.",
  releaseSelected: (n: number) => "Release " + String(n) + " selected",
  whenYouReleaseThese: "When you release these marks",
  releasedTo: (name: string) => "Released to " + name,
  // TQR-18 and TQR-23: a held learner's account, before release and in its receipt.
  heldBeforeRelease: (name: string, showsAnswers: boolean) => `${name}'s result is released for counting, but ${name} can't read ${showsAnswers ? "its score, comment, correct answers or explanations" : "its score or comment"} until their replacement on the same questions is submitted or can no longer start.`,
  heldInReceipt: (name: string, attempt: number | null) => `${name}'s ${attempt == null ? "result" : `attempt ${attempt}`} is released for counting, but held from ${name} until their replacement on the same questions is submitted or can no longer start.`,
  inTheMarkerSPlace: " in the marker's place",
  notInThisRelease: (name: string, attempt: string, why: string) => "Not in this release: " + name + attempt + " (" + why + ").",
  attemptN: (n: number) => ", attempt " + String(n),
  notMarked2: "not marked",
  heldNotChosen: "held, not chosen",
  releaseRound: (round: number) => "Release round " + String(round),
  roundReleased: (round: number) => `Round ${round} released`,
  recordedWord: "Recorded",
  recordedWhen: "Recorded ",
  readyToRelease: (n: number) => `${n} ready to release`,
  releasesIt: (who: string) => `${who} releases`,
  recordedHeldState: "Recorded, held",
  releaseLabel: "Release",
  whereReleaseStands: "Where this work stands for release, and each round released: who released it, when, and the exact attempts.",
  nothingReadyToRelease: "Nothing ready to release",
  nothingReadyToReleaseYet: "Nothing ready to release yet",
  attemptWaits: (name: string, attempt: string, why: string) => `${name}${attempt}: ${why}`,
  attemptsNotReady: (n: number) => `${n} attempts not ready`,
  releasedNothingElseReady: (n: number) => `${n} released · nothing else ready`,
  notConfirmedCount: (n: number) => `${n} not confirmed`,
  notConfirmedReason: "not confirmed",
  lastRelease: (round: number, who: string, when: string, released: string) => `Last release: round ${round}, by ${who}, ${when}: released to ${released}.`,
  earlierRelease: (round: number, who: string, when: string, released: string) => `Round ${round}, by ${who}, ${when}: released to ${released}.`,
  you: "you",
  aTeacher: "a teacher",
  releaseNotConfirmed: "Release not confirmed",
  theReleaseMayHaveGoneOutCheckIt: "The release may have gone out. Check reads this round's receipt; it never releases again or widens the round.",
  checkRelease: "Check release",
  nothingWasReleased: "Nothing was released",
  noReleaseWasRecordedForThisRequest: "No release was recorded for this request. Your selection is kept.",
  onlyTheseAttemptsAreReleased: "Only these attempts are released; everything else stays as it was.",
  readingTheReleaseRounds: "Reading the release rounds.",
  theNextReleaseRoundCouldNotBeRead: "The release rounds could not be read. Read them again before releasing.",
  releaseToEachLearnerNow: "Release to each learner now",
  recordSelected: (slot0: unknown) => "Record " + String(slot0) + " selected",
  whenYouRecordTheseJudgements: "When you record these judgements",
  releaseTheHeldJudgementTo: (slot0: unknown) => "Release the held judgement to " + String(slot0) + ".",
  recordMissingFor: (slot0: unknown) => "Record Missing for " + String(slot0) + ".",
  hasNoHeldJudgementToRelease: (slot0: unknown) => "" + String(slot0) + " has no held judgement to release.",
  complete: "Complete",
  returnForRevision: "Return for revision",
  grade: "Grade",
  chooseKeepMineOrUseTheOneOnRecord: "Choose Keep mine or Use the one on record first.",
  handedIn3: "Handed in ",
  onRecord: "On record: ",
  recorded: "· Recorded ",
  released2: (slot0: unknown) => " · Released " + String(slot0) + "",
  notReleased: " · Not released",
  someoneJudgedThisAttemptWhileYouWereWritingYour: "Someone judged this attempt while you were writing. Your judgement is kept here.",
  missingNothingHandedIn: "Missing, nothing handed in",
  reviseTheJudgement: "Revise the judgement",
  rubric: "Rubric",
  score2: "Score ",
  waitsForEveryCriterion: "waits for every criterion",
  commentFor: "Comment for ",
  releaseTo: "Release to ",
  now: " now",
  yourJudgementIsKeptHere: " Your judgement is kept here.",
  fold: "Fold",
  hide: "Hide",
  teacherRecord: "teacher record",
  recorded2: " · Recorded ",
  notReleased2: "Not released",
  noRecoveryOwner: "No recovery owner",
  scored: "Scored",
  scoredWithARubric: "Scored with a rubric",
  completion: "Completion",
  inClass: "In class",
  gradedOf: (slot0: unknown, slot1: unknown) => "Graded " + String(slot0) + " of " + String(slot1) + "",
  graded: "Graded",
  notTaken: "Not taken",
  taken: "Taken",
  attempt2: (slot0: unknown, slot1: unknown, slot2: unknown) => "Attempt " + String(slot0) + " · " + String(slot1) + "" + String(slot2) + "",
  attempt3: (slot0: unknown, slot1: unknown) => "Attempt " + String(slot0) + " · " + String(slot1) + "",
  markedByYourInstructor: (slot0: unknown) => "Marked by your instructor " + String(slot0) + "",
  assessedInClass2: (slot0: unknown) => "Assessed in class " + String(slot0) + "",
  closedToNewAttempts: "Closed to new attempts",
  termClosed: "Term closed",
  notMarkedAsTaken: "Not marked as taken",
  notMarkedDone: "Not marked done",
  missingUntilYouHandItIn: "Missing until you hand it in",
  attemptNotHandedIn: (slot0: unknown) => "Attempt " + String(slot0) + " not handed in",
  reviewAndHandInAttempt: (slot0: unknown) => "Review and hand in attempt " + String(slot0) + "",
  pastDue: "Past due",
  countsToward: (slot0: unknown) => "Counts toward: " + String(slot0) + "",
  yourSoFar: (slot0: unknown) => "Your " + String(slot0) + " so far",
  soFarOnThisLine: "So far on this line",
  recordedAgainst: "Recorded against",
  attemptAnswered: (slot0: unknown) => "Attempt " + String(slot0) + " answered",
  recordedByTheTeacherNoHandIn: "Recorded by the teacher, no hand-in",
  handedIn4: (slot0: unknown, slot1: unknown, slot2: unknown) => "" + String(slot0) + "Handed in " + String(slot1) + "" + String(slot2) + "",
  attempt4: (slot0: unknown) => "Attempt " + String(slot0) + " · ",
  enterAScoreOf0OrMore: "Enter a score of 0 or more",
  moreThanPoints: (slot0: unknown) => "More than " + String(slot0) + " points",
  releaseHeldNotRecorded: "Release held, not recorded",
  toReviewNotRecorded: (slot0: unknown, slot1: unknown, slot2: unknown) => "To review · " + String(slot0) + "" + String(slot1) + "" + String(slot2) + ", not recorded",
  recordJudgement: "Record judgement",
  recordNotReleased: "Record, not released",
  recordScore: "Record score",
  recordGrade: "Record grade",
  chooseHowToJudgeItFirst: "Choose how to judge it first.",
  scoreEveryCriterionFirst: "Score every criterion first.",
  enterTheScoreFirst: "Enter the score first.",
  whileTheWorkIsOpenCanStillHandIt: (slot0: unknown) => " While the work is open, " + String(slot0) + " can still hand it in.",
  recordedNotReleasedStillReads: (slot0: unknown, slot1: unknown) => "Recorded, not released. " + String(slot0) + " still reads " + String(slot1) + ".",
  googleDriveReturnedAFileThatCannotBeUsed: "Google Drive returned a file that cannot be used. Paste a link instead.",
  /** One sentence per reason the chooser came back empty; the link field stays beside each. */
  driveDeclined: "You didn't allow Google Drive access, so no file was chosen. Choose again and allow it, or paste a link.",
  drivePopupBlocked: "Your browser blocked the Google sign-in window. Allow pop-ups for this site and choose again, or paste a link.",
  driveNoToken: "Google didn't return access to your Drive. Sign in to Google and choose again, or paste a link.",
  driveUnresponsive: "Google Drive didn't respond. Try again later, or paste a link.",
  driveSelectionUnavailableOnThisSite: "Google Drive selection is unavailable on this site. Paste a Drive or other link instead.",
  /** A Drive reference as IMAM holds it: never a claim that this reader can open it. */
  googleDecidesWhoCanOpenThisFile: "Google decides who can open this file.",
  manageAccessInGoogleDrive: "Manage access in Google Drive",
  learnersSeeThisOnceTheSessionPlanIsPublished: "Learners see this once the session plan is published.",
  addATitle: "Add a title",
  useALinkThatStartsWithHttps: "Use a link that starts with https://",
  type: "Type",
  from: (slot0: unknown, slot1: unknown, slot2: unknown, slot3: unknown) => "" + String(slot0) + ": " + String(slot1) + "%, from " + String(slot2) + "" + String(slot3) + "",
  of2: (slot0: unknown, slot1: unknown, slot2: unknown) => "" + String(slot0) + " " + String(slot1) + " of " + String(slot2) + "",
  theTermsWereRestated: "the terms were restated",
  points3: (slot0: unknown) => "" + String(slot0) + " points",
  late: " · Late",
  andMore: (slot0: unknown) => " and " + String(slot0) + " more",
  withAComment: " with a comment",
  releaseHeld: ", release held",
  aReturnNeedsACommentSayingWhatToChange: "A return needs a comment saying what to change.",
  enterAScoreOf0OrMore2: (slot0: unknown) => "" + String(slot0) + ": enter a score of 0 or more",
  isMoreThanPoints: (slot0: unknown, slot1: unknown) => "" + String(slot0) + " is more than " + String(slot1) + " points",
  notHandedInNotCountedYet: (slot0: unknown, slot1: unknown) => "" + String(slot0) + " · " + String(slot1) + " not handed in, not counted yet",
  readsMissingMarkedByYourInstructorItCounts0: (slot0: unknown, slot1: unknown, slot2: unknown) => "" + String(slot0) + " reads Missing · Marked by your instructor. It counts 0" + String(slot1) + "." + String(slot2) + "",
  onWhichBecomes: (slot0: unknown, slot1: unknown) => " on " + String(slot0) + ", which becomes " + String(slot1) + "%",
  readsYourComment: (slot0: unknown, slot1: unknown) => "" + String(slot0) + " reads your comment" + String(slot1) + ".",
  andCanHandInAttempt: (slot0: unknown) => " and can hand in attempt " + String(slot0) + "",
  readsComplete: (slot0: unknown, slot1: unknown) => "" + String(slot0) + " reads Complete" + String(slot1) + ".",
  andYourComment: " and your comment",
  reads: (slot0: unknown, slot1: unknown, slot2: unknown) => "" + String(slot0) + " reads " + String(slot1) + "" + String(slot2) + ".",
  recordedAndReleased: "recorded and released",
  recordedNotReleased: "recorded, not released",
  notConfirmedCheckAgainBeforeSendingItAgain: "not confirmed; check again before sending it again",
  notRecordedYourEntryIsKept: (slot0: unknown) => "not recorded" + String(slot0) + ", your entry is kept",
  planNotPublished: " (plan not published)",
  yourWrittenResponse: "Your written response",
  bold: "Bold",
  italic: "Italic",
  quotation: "Quotation",
  bulletList: "Bullet list",
  numberedList: "Numbered list",
  paragraphDirection: "Paragraph direction",
  autoDirection: "Auto direction",
  leftToRight: "Left to right",
  rightToLeft: "Right to left",
  undo: "Undo",
  redo: "Redo",
  writingTools: "Writing tools",
  paragraphStyle: "Paragraph style",
  paragraph: "Paragraph",
  section: "Section",
  subsection: "Subsection",
  textFormatting: "Text formatting",
  linkAddress: "Link address",
  https: "https://",
  removeLink: "Remove link",
  apply: "Apply",
  listsAndQuotation: "Lists and quotation",
  writingHistory: "Writing history",
  moreFormatting: "More formatting",
  beginYourResponseHere: "Begin your response here.",
  useTheBriefAsAGuideYourWritingStaysPrivate: "Use the brief as a guide. Your writing stays private until you submit.",
  correctedTerms: "Corrected terms",
  updated: "Updated ",
  earlierSubmissionsKeepTheTermsThatAppliedWhenTheyWere: ". Earlier submissions keep the terms that applied when they were submitted.",
  why: "Why: ",
  earlierTermsInForceFrom: "Earlier terms, in force from ",
  anySubmission: "Any submission",
  pts: " pts",
  viewProvisionalResult: "View provisional result",
  theLinkSHostDecidesWhoCanOpenIt: "The link's host decides who can open it.",
  thisIsNotAWebAddressSoItCannotBeOpened: "This is not a web address, so it cannot be opened from here.",
  thisFileIsNoLongerAvailable: "This file is no longer available.",
  allowANewTabToOpenThisFileThenTryAgain: "Allow a new tab to open this file, then try again.",
  factSeparator: " · ",
  loadingFeedback: "Loading feedback...",
  feedbackCouldNotBeRead: "Feedback could not be read. ",
  reviewAttempt: "Review attempt",
  releaseToTheLearner: "Release to the learner",
  reviseJudgement: "Revise judgement",
  judgementOnRecord: "Judgement on record",
  commentOnRecord: "Comment on record",
  judgementFor: "Judgement for ",
  comment: "Comment",
  whatTheLearnerShouldTakeFromThisAttempt: "What the learner should take from this attempt…",
  releaseToTheLearnerNow: "Release to the learner now",
  cancel: "Cancel",
  loadingThisWork: "Loading this work",
  thisWorkIsUnavailableInThisCourse: "This work is unavailable in this course.",
  remove: "Remove",
  publish: "Publish",
  closeToNewAttempts: "Close to new attempts",
  loadingAttempts: "Loading attempts...",
  theAttemptsForThisWorkCouldNotBeLoaded: "The attempts for this work could not be loaded.",
  reviewLearners: "Review learners",
  backToAttempts: "Back to attempts",
  attempts: "Attempts",
  noLearnerHasHandedInThisWorkYet: "No learner has handed in this work yet.",
  attempts2: "Attempts ",
  thisAttemptIsUnavailableInThisWork: "This attempt is unavailable in this work.",
  newWork: "New work",
  add: "Add",
  discard: "Discard",
  retry: "Retry",
  openInGradebook: "Open in Gradebook",
  kind: "Kind",
  itsHostGovernsAccessTheScoreIsRecordedInIMAM: "Its host governs access. The score is recorded in IMAM.",
  nothingToHandInTheTeacherRecordsTheScore: "Nothing to hand in. The teacher records the score.",
  criterion2: "Criterion ",
  points4: "Points ",
  criterion3: "+ Criterion",
  optionalCriterionPointsAddUpToTheWorkSPoints: "Optional. The criteria's points make the work's total.",
  closeParenthesis: ")",
  learnersSeeTheCorrectedTermsNotThisReason: "Learners see the corrected terms, not this reason.",
  saveCorrection: "Save correction",
  countsToward2: "Counts toward ",
  useANumberFrom0To999999: "Use a number from 0 to 9999.99",
  materials: "Materials",
  source: "Source",
  https2: "https://...",
  discardUnsavedChangesAndLeaveThisRecord: "Discard unsaved changes and leave this record?",
  notSet2: "Not set",
  isClosedSoWorkCannotBeHandedInToIts: (slot0: unknown) => "" + String(slot0) + " is closed, so work cannot be handed in to its assignments. You can still read your work and feedback.",
  attestationWhen: (slot0: unknown, slot1: unknown) => "" + String(slot0) + " " + String(slot1) + "",
  released3: (slot0: unknown, slot1: unknown) => "" + String(slot0) + " released " + String(slot1) + "",
  notHandedInNotCountedYet2: (slot0: unknown) => " · " + String(slot0) + " not handed in, not counted yet",
  theDueTimeIsNow: (slot0: unknown) => "the due time is now " + String(slot0) + "",
  thePointsAreNow: (slot0: unknown) => "the points are now " + String(slot0) + "",
  theAssessmentIsNow: (slot0: unknown) => "the assessment is now " + String(slot0) + "",
  and: (slot0: unknown, slot1: unknown) => "" + String(slot0) + " and " + String(slot1) + "",
  leavingAlsoDiscards: (slot0: unknown) => "Leaving also discards " + String(slot0) + ".",
  withheld: "Withheld",
  withheldUntil: (slot0: unknown) => "Withheld until " + String(slot0) + "",
  workAddedAsADraft: "Work added as a draft",
  couldNotConfirmThisWorkWasAddedCheckCourseworkBefore: "Could not confirm this work was added. Check Coursework before trying again.",
  saving: "Saving",
  saved: "Saved",
  notSaved: "Not saved",
  theSessionsCouldNotBeRead: "The sessions could not be read.",
  thisCourseHasNoSessionsYet: "This course has no sessions yet.",
  theGradeItemsCouldNotBeRead: "The grade items could not be read.",
  thisCourseHasNoGradeItemsYet: "This course has no grade items yet.",
  removeCriterion2: (slot0: unknown) => "Remove criterion " + String(slot0) + "",
  kindQuizLinkAndRubric: "Kind, quiz link and rubric",
  gradedWorkNeedsPointsAboveZero: "Graded work needs points above zero.",
  useAnHTTPSQuizLinkWithoutCredentials: "Use an HTTPS quiz link without credentials.",
  addTheHTTPSQuizLink: "Add the HTTPS quiz link.",
  eachRubricCriterionNeedsALabelAndPointsAboveZero: "Each rubric criterion needs a label and points above zero (up to 20 criteria).",
  noPoints: "No points",
  weight: (slot0: unknown, slot1: unknown, slot2: unknown) => "" + String(slot0) + " · " + String(slot1) + " · weight " + String(slot2) + "",
  thisSession: "This session",
  thisGradeItem: "This grade item",
  notPublishedAChangeHereIsNotSavedRetryIt: "Not published. A change here is not saved: retry it or put it back, then publish.",
  youCanNoLongerEditThisWorkYourEditsAre: "You can no longer edit this work. Your edits are still here. Refresh to check your access.",
  thisWorkChangedBeforeYourEditsWereSavedYourEdits: "This work changed before your edits were saved. Your edits are still here. Refresh to see it as it stands.",
  couldNotPublishThisWork: "Could not publish this work.",
  closedToNewAttempts2: (slot0: unknown) => "Closed " + String(slot0) + " to new attempts",
  couldNotCloseThisWork: "Could not close this work.",
  removed: (slot0: unknown) => "Removed " + String(slot0) + "",
  couldNotRemoveThisWork: "Could not remove this work.",
  closed: "Closed",
  edit: (slot0: unknown) => "Edit " + String(slot0) + "",
  moreFor: (slot0: unknown) => "More for " + String(slot0) + "",
  due6: (slot0: unknown) => "Due " + String(slot0) + "",
  thisTermIsClosedSoWorkCannotBePublishedIn: "This term is closed, so work cannot be published in it.",
  thisWorkIsClosedSoAReturnWouldInviteAn: "This work is closed, so a return would invite an attempt that cannot be made.",
  thisTermIsClosedSoAReturnWouldInviteAn: "This term is closed, so a return would invite an attempt that cannot be made.",
  workPublished: "Work published",
  enrolledLearner: "Enrolled learner",
  review3: (slot0: unknown) => "Review " + String(slot0) + "",
  uploadedFile: "Uploaded file",
  couldNotPrepareTheDownload: "Could not prepare the download",
  thisAttemptHasAlreadyBeenJudged: "This attempt has already been judged.",
  theLearnerHasSinceHandedInANewerAttempt: "The learner has since handed in a newer attempt.",
  thisAttemptBelongsToOtherWorkInThisCourse: "This attempt belongs to other work in this course.",
  youAreNoLongerThisCourseSInstructorSoThis: "You are no longer this course's instructor, so this attempt is not yours to judge.",
  thisAttemptSReviewStateCouldNotBeRead: "This attempt's review state could not be read.",
  teacherRecord2: "Teacher record",
  attempt5: (slot0: unknown) => "attempt " + String(slot0) + "",
  recorded3: "Recorded",
  judgementReleased: "Judgement released",
  couldNotReleaseTheJudgementTryAgain: "Could not release the judgement. Try again.",
  replacedByAttempt: (slot0: unknown) => "Replaced by attempt " + String(slot0) + "",
  late2: "Late",
  gradeRecorded: "Grade recorded",
  couldNotRecordTheJudgementTryAgain: "Could not record the judgement. Try again.",
  by: (slot0: unknown, slot1: unknown) => "by " + String(slot0) + ", " + String(slot1) + "",
  attempt6: (slot0: unknown) => "attempt " + String(slot0) + "",
  score3: (slot0: unknown) => "" + String(slot0) + "-score",
  outOf: (slot0: unknown) => " (out of " + String(slot0) + ")",
  stillReadsReturnedForRevisionAndCanHandInAttempt: (slot0: unknown, slot1: unknown) => "" + String(slot0) + " still reads: Returned for revision and can hand in attempt " + String(slot1) + "",
  attempt7: (slot0: unknown) => "attempt " + String(slot0) + "",
  answeredTermsSummary: (slot0: unknown, slot1: unknown) => "" + String(slot0) + ": " + String(slot1) + "",
  outcomeReleaseSummary: (slot0: unknown, slot1: unknown) => "" + String(slot0) + " · " + String(slot1) + "",
  recordedAttestationWhen: (slot0: unknown, slot1: unknown) => "" + String(slot0) + " " + String(slot1) + "",
  ofAssessedInClass: (slot0: unknown, slot1: unknown) => "" + String(slot0) + " of " + String(slot1) + " assessed in class",
  ofTaken: (slot0: unknown, slot1: unknown) => "" + String(slot0) + " of " + String(slot1) + " taken",
  ofHandedIn: (slot0: unknown, slot1: unknown) => "" + String(slot0) + " of " + String(slot1) + " handed in",
  markedDone2: (slot0: unknown) => "" + String(slot0) + " marked done",
  recordedByTheTeacher: (slot0: unknown) => "" + String(slot0) + " recorded by the teacher",
  assessedInClass3: (slot0: unknown) => "" + String(slot0) + " assessed in class",
  handedIn5: (slot0: unknown) => "" + String(slot0) + " handed in",
  markedMissing: (slot0: unknown) => "" + String(slot0) + " marked missing",
  missingLearnersSummary: (slot0: unknown, slot1: unknown, slot2: unknown) => "" + String(slot0) + ": " + String(slot1) + "" + String(slot2) + "",
  sSoFarNothingReleasedYet: (slot0: unknown, slot1: unknown) => "" + String(slot0) + "'s " + String(slot1) + " so far: nothing released yet",
  sSoFarFromReleasedScore: (slot0: unknown, slot1: unknown, slot2: unknown, slot3: unknown, slot4: unknown) => "" + String(slot0) + "'s " + String(slot1) + " so far: " + String(slot2) + "%, from " + String(slot3) + " released score" + String(slot4) + "",
  s: (slot0: unknown) => "" + String(slot0) + "s",
  countedWord: (slot0: unknown, slot1: unknown) => "" + String(slot0) + " " + String(slot1) + "",
  published2: (slot0: unknown) => "" + String(slot0) + " published",
  draft2: (slot0: unknown, slot1: unknown) => "" + String(slot0) + " draft" + String(slot1) + "",
  materialFacts: (slot0: unknown, slot1: unknown, slot2: unknown) => "" + String(slot0) + "" + String(slot1) + "" + String(slot2) + "",
  for: (slot0: unknown) => ", for " + String(slot0) + "",
  new: (slot0: unknown) => "New " + String(slot0) + "",
  draftRemoved: "Draft removed",
  couldNotRemoveThisMaterial: "Could not remove this material.",
  sessionPlanState: (slot0: unknown, slot1: unknown) => "" + String(slot0) + "" + String(slot1) + "",
  yourUnsavedAssignmentDraft: "your unsaved assignment draft",
  writeStillInProgress: "A write is still in progress. Wait for its result before leaving this record.",
  anotherInstructor: "another instructor",
  thisAttempt: "this attempt",
  newerReadNeededBeforeDeciding: "A newer read is needed before deciding",
  theChosenSession: "the chosen session",
  theWholeCourse: "the whole course",
  wholeCourseFact: " · Whole course",
  thisReference: "this material",
  asTheCourseSyllabus: " as the course syllabus",
  theDueTimeIsRemoved: "the due time is removed",
  thePointsAreRemoved: "the points are removed",
  theBriefChanged: "the brief changed",
  whatYouHandInChanged: "what you hand in changed",
  theQuizLinkChanged: "the quiz link changed",
  theRubricChanged: "the rubric changed",
  nothingYet: "nothing yet",
  storedSubmissions: (count: number) => `${count} stored ${count === 1 ? "submission" : "submissions"}`,
  postedItemCount: (count: number) => `${count} ${count === 1 ? "item" : "items"}`,
  waitingForRecord: (count: number) => `${count} waiting for Record in IMAM`,
  courseworkCount: (count: number) => `${count} ${count === 1 ? "work item" : "work items"}`,
  selectedCount: (count: number) => `${count} selected`,
} as const;
